import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, RefreshCw, DollarSign, Activity } from "lucide-react";

export default function ManualTradingPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [balance, setBalance] = useState(null);

  const [trade, setTrade] = useState({
    api_key_id: "",
    symbol: "NOMOXUSDT",
    side: "BUY",
    order_type: "LIMIT",
    quantity: "",
    price: "",
  });

  useEffect(() => {
    fetchKeys();
    fetchCurrentPrice();
  }, []);

  useEffect(() => {
    if (trade.api_key_id) {
      fetchBalance();
    }
  }, [trade.api_key_id]);

  const fetchKeys = async () => {
    try {
      const r = await api.get("/keys");
      setKeys(r.data);
    } catch (e) {
      toast.error("Failed to load API keys");
    }
  };

  const fetchCurrentPrice = async () => {
    try {
      const r = await api.get("/market/ticker", { params: { symbol: trade.symbol } });
      setCurrentPrice(parseFloat(r.data.price));
    } catch (e) {
      console.error("Failed to get price:", e);
    }
  };

  const fetchBalance = async () => {
    try {
      const r = await api.get("/market/balance", { params: { key_id: trade.api_key_id } });
      setBalance(r.data);
    } catch (e) {
      console.error("Failed to get balance:", e);
    }
  };

  const placeOrder = async () => {
    if (!trade.api_key_id) {
      toast.error("Please select an API key");
      return;
    }
    if (!trade.quantity || parseFloat(trade.quantity) <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }
    if (trade.order_type === "LIMIT" && (!trade.price || parseFloat(trade.price) <= 0)) {
      toast.error("Please enter a valid price for limit order");
      return;
    }

    setPlacing(true);
    try {
      const payload = {
        api_key_id: trade.api_key_id,
        symbol: trade.symbol,
        side: trade.side,
        order_type: trade.order_type,
        quantity: parseFloat(trade.quantity),
      };

      if (trade.order_type === "LIMIT") {
        payload.price = parseFloat(trade.price);
      }

      const r = await api.post("/manual-trade", payload);
      toast.success(r.data.message || "Order placed successfully");
      
      // Reset form
      setTrade({ ...trade, quantity: "", price: "" });
      
      // Refresh data
      fetchBalance();
      fetchCurrentPrice();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to place order");
    } finally {
      setPlacing(false);
    }
  };

  const calculateTotal = () => {
    const qty = parseFloat(trade.quantity) || 0;
    const price = trade.order_type === "MARKET" 
      ? (currentPrice || 0) 
      : (parseFloat(trade.price) || 0);
    return (qty * price).toFixed(4);
  };

  return (
    <div className="space-y-6" data-testid="manual-trading-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Manual Trading</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Place direct buy/sell orders on MEXC</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchCurrentPrice(); fetchBalance(); }} className="w-full sm:w-auto">
          <RefreshCw className="w-3.5 h-3.5 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Trading Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Place Order
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* API Key Selection */}
              <div>
                <Label>API Key</Label>
                <Select value={trade.api_key_id} onValueChange={(v) => setTrade({ ...trade, api_key_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select API key" />
                  </SelectTrigger>
                  <SelectContent>
                    {keys.map((k) => (
                      <SelectItem key={k.id} value={k.id}>
                        {k.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Symbol */}
              <div>
                <Label>Trading Pair</Label>
                <Input
                  value={trade.symbol}
                  onChange={(e) => setTrade({ ...trade, symbol: e.target.value.toUpperCase() })}
                  placeholder="NOMOXUSDT"
                  onBlur={fetchCurrentPrice}
                />
              </div>

              {/* Side and Order Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Side</Label>
                  <Select value={trade.side} onValueChange={(v) => setTrade({ ...trade, side: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BUY">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-emerald-500" />
                          <span>Buy</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="SELL">
                        <div className="flex items-center gap-2">
                          <TrendingDown className="w-4 h-4 text-red-500" />
                          <span>Sell</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Order Type</Label>
                  <Select value={trade.order_type} onValueChange={(v) => setTrade({ ...trade, order_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LIMIT">Limit Order</SelectItem>
                      <SelectItem value="MARKET">Market Order</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Quantity */}
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={trade.quantity}
                  onChange={(e) => setTrade({ ...trade, quantity: e.target.value })}
                  placeholder="1000"
                />
              </div>

              {/* Price (only for limit orders) */}
              {trade.order_type === "LIMIT" && (
                <div>
                  <Label>Price (USDT)</Label>
                  <Input
                    type="number"
                    step="0.00000001"
                    value={trade.price}
                    onChange={(e) => setTrade({ ...trade, price: e.target.value })}
                    placeholder={currentPrice ? currentPrice.toFixed(8) : "0.00000001"}
                  />
                  {currentPrice && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Current market price: {currentPrice.toFixed(8)} USDT
                    </p>
                  )}
                </div>
              )}

              {/* Total */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Estimated Total:</span>
                  <span className="text-lg font-bold">{calculateTotal()} USDT</span>
                </div>
                {trade.order_type === "MARKET" && currentPrice && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Market orders execute at current price: {currentPrice.toFixed(8)} USDT
                  </p>
                )}
              </div>

              {/* Place Order Button */}
              <Button
                className={`w-full text-base py-6 ${
                  trade.side === "BUY"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
                onClick={placeOrder}
                disabled={placing || !trade.api_key_id}
              >
                {placing ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : trade.side === "BUY" ? (
                  <TrendingUp className="w-4 h-4 mr-2" />
                ) : (
                  <TrendingDown className="w-4 h-4 mr-2" />
                )}
                {placing ? "Placing Order..." : `${trade.side} ${trade.symbol}`}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right: Market Info & Balance */}
        <div className="space-y-4">
          {/* Current Price */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Market Price</CardTitle>
            </CardHeader>
            <CardContent>
              {currentPrice ? (
                <div>
                  <div className="text-2xl font-bold">{currentPrice.toFixed(8)}</div>
                  <div className="text-sm text-muted-foreground">USDT</div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">Loading...</div>
              )}
            </CardContent>
          </Card>

          {/* Balance */}
          {balance && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Account Balance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {balance.balances && balance.balances.length > 0 ? (
                  balance.balances.slice(0, 5).map((b, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{b.asset}</span>
                      <span className="font-medium">{parseFloat(b.free).toFixed(4)}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-muted-foreground">No balance data</div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quick Tips */}
          <Card className="border-blue-500/20 bg-blue-500/5">
            <CardHeader>
              <CardTitle className="text-base">Quick Tips</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>• <strong>Limit Orders:</strong> Set your own price</p>
              <p>• <strong>Market Orders:</strong> Execute at current price</p>
              <p>• Orders are logged in Activity Logs</p>
              <p>• Check your MEXC account for confirmation</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
